import React, { useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import { Button, Heading, Link, theme } from "@icg360/design-system";
import { styled } from "styled-components";

import { track } from "../../units/tracking";
import { Footer } from "../footer";

interface LoginFormProps {
  enabled?: boolean;
  isLoading?: boolean;
  message?: string;
  messageType?: string;
  view?: string;
  onViewChange: (view: string) => void;
  onLoginSubmit: (username: string, password: string) => void;
  onForgotPasswordSubmit: (username: string) => void;
  onForgotUsernameSubmit: (email: string) => void;
  supportPhoneHTML?: string;
}

const StyledField = styled.div<{ noLabel?: boolean; bottomPadding?: boolean }>`
  display: block;
  transition: opacity 0.2s;

  ${({ noLabel }) => noLabel && "padding-top: 30px;"}
  ${({ bottomPadding }) => bottomPadding && "padding-bottom: 40px;"}
`;

const StyledInlineFields = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.lg};
`;

const StyledForm = styled.form`
  color: ${theme.colors.text.default};

  &[data-enabled="false"],
  ${StyledField}[data-enabled="false"] {
    opacity: 0.3;
  }
`;

const StyledInput = styled.div`
  input {
    padding: 0 0 0 10px;
    height: 50px;
    width: 250px;
    border: 1px solid ${theme.colors.border.control};
    border-radius: 4px;
    font-size: 20px;
    color: $black;
    &:focus {
      outline: 1px solid ${theme.colors.border.interactive};
    }
  }
`;

const StyledLinkWrapper = styled.div`
  padding: 9px 0 7px;
`;

const FooterWrap = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
`;

export const LoginForm = ({
  enabled = true,
  isLoading = false,
  message = "",
  messageType = "",
  view = "",
  onViewChange,
  onLoginSubmit,
  onForgotPasswordSubmit,
  onForgotUsernameSubmit,
  supportPhoneHTML = ""
}: LoginFormProps) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const shouldFocusUsername = useRef(true);
  const containerRef = useRef<HTMLDivElement>();
  const formRef = useRef<HTMLDivElement>();
  const usernameRef = useRef<HTMLInputElement>();

  useEffect(() => {
    animateHeight();
    focusUsername();

    // on resize callback
    window.addEventListener("resize", animateHeight);

    // clean up
    return () => window.removeEventListener("resize", animateHeight);
  }, []);

  const handleLoginSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (enabled && !isLoading) {
      track("Login: Login Button");
      onLoginSubmit(username, password);
    }
  };

  const handleForgotPasswordSubmit = (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    if (enabled && !isLoading) {
      track("Login: Reset Password");
      onForgotPasswordSubmit(username);
    }
  };

  const handleForgotUsernameSubmit = (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    if (enabled && !isLoading) {
      track("Login: Retrieve Username");
      onForgotUsernameSubmit(email);
    }
  };

  const handleForgotPasswordViewClick = (
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    event.preventDefault();
    if (enabled && !isLoading) {
      track("Login: Forgot Password");
      onViewChange("forgot-password");
    }
  };

  const handleForgotUsernameViewClick = (
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    event.preventDefault();
    if (enabled && !isLoading) {
      track("Login: Forgot Username");
      onViewChange("forgot-username");
    }
  };

  const handleLoginViewClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (enabled && !isLoading) {
      onViewChange("");
    }
  };

  const animateHeight = () => {
    if (containerRef.current && formRef.current) {
      containerRef.current.style.height = `${formRef.current.offsetHeight}px`;
    }
  };

  const focusUsername = () => {
    if (shouldFocusUsername && usernameRef) {
      usernameRef.current.focus();
      shouldFocusUsername.current = false;
    }
  };

  const renderForm = () => {
    if (view === "forgot-username") {
      return (
        <StyledForm
          className="LoginForm SageSure-only"
          data-enabled={enabled}
          onSubmit={handleForgotUsernameSubmit}
        >
          <div className="LoginForm-help">
            <Heading tag="h4" size="sm">
              Retrieve your username
            </Heading>
            <p>
              Enter your account email below and we will send you an email
              detailing the username(s) associated with your account email.
            </p>
            <p>
              If you cannot remember your account email, please contact Policy
              Services at{" "}
              <span dangerouslySetInnerHTML={{ __html: supportPhoneHTML }} />
            </p>
          </div>
          <StyledInlineFields>
            <StyledField>
              <div className="title">
                <label htmlFor="forgot-username">Account Email</label>
              </div>
              <StyledInput>
                <input
                  disabled={!enabled}
                  id="forgot-username"
                  onChange={e => setEmail(e.target.value)}
                  ref={usernameRef}
                  type="text"
                  value={email}
                />
              </StyledInput>
            </StyledField>
            <StyledField noLabel>
              <Button
                appearance="primary"
                type="submit"
                data-loading={isLoading ? "on" : "off"}
                disabled={!enabled || isLoading}
              >
                Retrieve Username
              </Button>
            </StyledField>
            <StyledField noLabel data-enabled={!isLoading}>
              <div className="link">
                <Link href="#" onClick={handleLoginViewClick}>
                  Back to Login
                </Link>
              </div>
            </StyledField>
          </StyledInlineFields>
        </StyledForm>
      );
    }
    if (view === "forgot-password") {
      return (
        <StyledForm
          className="LoginForm SageSure-only"
          data-enabled={enabled}
          onSubmit={handleForgotPasswordSubmit}
        >
          <div className="LoginForm-help">
            <Heading tag="h4" size="sm">
              Reset your password
            </Heading>
            <p>
              Enter your Username below. We will send an email to the address on
              record for that account.
            </p>
            <p>
              If you cannot remember your Username, please contact Policy
              Services at{" "}
              <span dangerouslySetInnerHTML={{ __html: supportPhoneHTML }} />
            </p>
          </div>
          <StyledInlineFields>
            <StyledField>
              <div className="title">
                <label htmlFor="login-username">Username</label>
              </div>
              <StyledInput>
                <input
                  disabled={!enabled}
                  id="forgot-username"
                  onChange={e => setUsername(e.target.value)}
                  ref={usernameRef}
                  type="text"
                  value={username}
                />
              </StyledInput>
            </StyledField>
            <StyledField noLabel>
              <Button
                appearance="primary"
                type="submit"
                data-loading={isLoading ? "on" : "off"}
                disabled={!enabled || isLoading}
              >
                Reset Password
              </Button>
            </StyledField>
            <StyledField noLabel data-enabled={!isLoading}>
              <div className="link">
                <Link href="#" onClick={handleLoginViewClick}>
                  Back to Login
                </Link>
              </div>
            </StyledField>
          </StyledInlineFields>
        </StyledForm>
      );
    }

    return (
      <StyledForm data-enabled={enabled} onSubmit={handleLoginSubmit}>
        <StyledInlineFields>
          <StyledField>
            <div className="title">
              <label htmlFor="login-username">Username</label>
            </div>
            <StyledInput>
              <input
                disabled={!enabled}
                id="login-username"
                onChange={e => setUsername(e.target.value)}
                ref={usernameRef}
                type="text"
                value={username}
                tabIndex={1}
              />
            </StyledInput>
            <div data-enabled={!isLoading}>
              <StyledLinkWrapper>
                <Link
                  href="#forgot-username"
                  onClick={handleForgotUsernameViewClick}
                >
                  Forgot username?
                </Link>
              </StyledLinkWrapper>
            </div>
          </StyledField>
          <StyledField>
            <div className="title">
              <label htmlFor="login-password">Password</label>
            </div>
            <StyledInput>
              <input
                disabled={!enabled}
                id="login-password"
                onChange={e => setPassword(e.target.value)}
                type="password"
                value={password}
                tabIndex={2}
              />
            </StyledInput>
            <div data-enabled={!isLoading}>
              <StyledLinkWrapper>
                <Link
                  href="#forgot-password"
                  onClick={handleForgotPasswordViewClick}
                >
                  Forgot password?
                </Link>
              </StyledLinkWrapper>
            </div>
          </StyledField>
          <StyledField noLabel bottomPadding>
            <Button
              appearance="primary"
              type="submit"
              data-loading={isLoading ? "on" : "off"}
              disabled={!enabled || isLoading}
              tabIndex={3}
            >
              Login
            </Button>
          </StyledField>
        </StyledInlineFields>
      </StyledForm>
    );
  };

  return (
    <>
      <div className="LoginFormContainer" ref={containerRef}>
        <div ref={formRef}>
          {renderForm()}

          {message && (
            <div className="LoginForm-message" data-type={messageType}>
              {message}
            </div>
          )}
        </div>
      </div>
      <FooterWrap>
        <Footer loggedIn={false} />
      </FooterWrap>
    </>
  );
};

LoginForm.propTypes = {
  enabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  message: PropTypes.string,
  messageType: PropTypes.string,
  onForgotUsernameSubmit: PropTypes.func.isRequired,
  onForgotPasswordSubmit: PropTypes.func.isRequired,
  onLoginSubmit: PropTypes.func.isRequired,
  onViewChange: PropTypes.func.isRequired,
  supportPhoneHTML: PropTypes.string,
  view: PropTypes.string
};
